import React, { useEffect, useState } from "react";
import { Button, Typography } from "@mui/material";
import { Layout, SectionContent, SectionHead } from "@layouts/MainLayout";
import { useTranslation } from "react-i18next";
import TableList from "@components/TableList";
import { RowSelectionState } from "@tanstack/react-table";
import { columns } from "@pages/user/unit_groups/_components/columns";
import { useQuery } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { AxiosError } from "axios";
import useHandleError from "@lib/utils/errorHook";
import { useSnackbar } from "@context/SnackBarProvider";
import { getUnitGroups } from "@lib/api/queries/unit_groups";
import { UnitGroup, UnitGroupsList } from "@lib/types/unit_group";
import CreateUnitGroup from "@pages/user/unit_groups/_components/createUnitGroup";
import ViewUnitGroup from "@pages/user/unit_groups/_components/viewUnitGroup";
import UpdateUnitGroup from "./_components/updateUnitGroup";

const UnitGroupsMain: React.FC = () => {
  const { t } = useTranslation();
  const { showSnackbar } = useSnackbar();
  const handleError = useHandleError();

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [addUnitGroupDialogOpen, setAddUnitGroupDialogOpen] = useState(false);
  const [updateUnitGroupDialogOpen, setUpdateUnitGroupDialogOpen] =
    useState(false);
  const [unitGroupToEdit, setUnitGroupToEdit] = useState<UnitGroup | undefined>(
    undefined
  );
  const [showDeletePrompt, setShowDeletePrompt] = useState(false);
  const [selectedUnitGroup, setSelectedUnitGroup] = useState<
    UnitGroup | undefined
  >(undefined);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);

  const {
    data: unitGroups,
    isFetching,
    refetch,
    error,
  } = useQuery<UnitGroupsList>({
    queryKey: ["unit_groups"],
    queryFn: () => getUnitGroups(),
    refetchOnWindowFocus: false,
  });

  // const { mutate: deleteDriverfn, isPending: isDeletingDriver } = useMutation({
  //   mutationFn: deleteDriver,
  //   onSuccess: (data: DeleteDriverResponse) => {
  //     showSnackbar(t("user.drivers.deleteSuccess"), "success");

  //     setSelectedUnitGroup(undefined);
  //     refetch();
  //   },
  //   onError: (error: AxiosError | unknown) => {
  //     if (error instanceof AxiosError && error.response) {
  //       const errorDetails = handleError(error.response.data);
  //       errorDetails && showSnackbar(errorDetails.message, "error");
  //     } else {
  //       showSnackbar(t("general.error"), "error");
  //     }
  //   },
  // });

  // const confirmDeleteDriver = () => {
  //   if (!selectedUnitGroup) return;
  //   deleteDriverfn(selectedUnitGroup.id);
  //   setShowDeletePrompt(false);
  // };

  // const handleDeleteDriver = (unitGroup: UnitGroup) => {
  //   setSelectedUnitGroup(unitGroup);
  //   setShowDeletePrompt(true);
  // };

  useEffect(() => {
    if (error instanceof AxiosError && error.response) {
      const errorDetails = handleError(error.response.data);
      errorDetails && showSnackbar(errorDetails.message, "error");
    }
  }, [error]);

  const handleViewUnitGroup = (unitGroup: UnitGroup) => {
    setSelectedUnitGroup(unitGroup);
    setViewDialogOpen(true);
  };

  return (
    <Layout>
      <SectionHead title={t("user.unitGroups.header")}>
        <div className="flex items-center gap-2">
          <Button
            variant="contained"
            onClick={() => {
              setAddUnitGroupDialogOpen(true);
            }}
            color="primary"
            className="flex items-center gap-1 p-2"
          >
            <Plus size={18} />
            <Typography variant="body2">{t("user.unitGroups.add")}</Typography>
          </Button>
        </div>
      </SectionHead>

      <SectionContent>
        {!error && (
          <div className="flex items-center justify-between">
            <TableList
              data={unitGroups?.data || []}
              columns={columns(
                setUnitGroupToEdit,
                setUpdateUnitGroupDialogOpen,
                handleViewUnitGroup
              )}
              rowSelection={rowSelection}
              setRowSelection={setRowSelection}
              isLoading={isFetching}
            />
          </div>
        )}

        <CreateUnitGroup
          addUnitGroupDialogOpen={addUnitGroupDialogOpen}
          setAddUnitGroupDialogOpen={setAddUnitGroupDialogOpen}
          refetch={refetch}
        />

        <UpdateUnitGroup
          updateUnitGroupDialogOpen={updateUnitGroupDialogOpen}
          setUpdateUnitGroupDialogOpen={setUpdateUnitGroupDialogOpen}
          unitGroupData={unitGroupToEdit}
          refetch={refetch}
        />

        <ViewUnitGroup
          viewUnitGroupDialogOpen={viewDialogOpen}
          setViewUnitGroupDialogOpen={setViewDialogOpen}
          unitGroupData={selectedUnitGroup}
        />
      </SectionContent>
    </Layout>
  );
};

export default UnitGroupsMain;
